/**
 * Dono unico da inscricao de Web Push deste navegador.
 *
 * Responsabilidade: descobrir se este navegador consegue receber notificacao,
 * pedir a permissao, criar/apagar a `PushSubscription` e mante-la sincronizada
 * com o servidor.
 *
 * NAO e responsabilidade deste arquivo: guardar estado (`store.ts`), desenhar
 * (`views/ajustes.ts`), nem decidir o que e um alerta — isso e do servidor, e o
 * texto da notificacao chega pronto no payload.
 *
 * ## A permissao so pode ser pedida uma vez
 *
 * `Notification.requestPermission()` negado nao pode ser pedido de novo: o
 * navegador passa a responder `denied` sem mostrar nada, e so as configuracoes
 * do site revertem. Por isso a chave VAPID e buscada ANTES do pedido — se o
 * servidor nao tem push configurado, a tela diz isso e nao gasta a unica
 * chance do usuario com uma permissao que nao ia servir para nada.
 *
 * ## iOS: a PWA precisa estar instalada
 *
 * O Safari so expoe `PushManager` para uma PWA aberta a partir da tela de
 * inicio (iOS 16.4+). Aberta na aba, `window.PushManager` simplesmente nao
 * existe — e `suportado: false` cobre esse caso junto com o navegador antigo,
 * porque a acao que a tela pede e a mesma nos dois: "instale o app".
 *
 * ## O que acontece se isto tudo falhar
 *
 * Nada de importante. O alerta continua nascendo no servidor, indo para o feed
 * e chegando pela tela aberta. E o bot do Telegram do `gfc` continua avisando —
 * ele e local-first e funciona ate com a nuvem fora do ar (§0). Esta camada e
 * conveniencia; nao pode virar dependencia.
 */

import * as api from "./api";
import * as store from "./store";

const suportado = (): boolean =>
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  typeof Notification !== "undefined";

/**
 * Le o estado real do navegador para dentro do store. Chamado no boot.
 *
 * "Ligado" e a existencia da inscricao NO NAVEGADOR, e nao o que o servidor
 * guardou: quem revoga por fora — limpar dados do site, desinstalar a PWA — e
 * o navegador, e o servidor so descobre quando o endpoint responde 410. A tela
 * tem que mostrar o que vale agora neste aparelho.
 */
export async function sincronizar(): Promise<void> {
  if (!suportado()) {
    store.definirPush({ suportado: false, ligado: false });
    return;
  }
  try {
    const registro = await navigator.serviceWorker.ready;
    const inscricao = await registro.pushManager.getSubscription();
    store.definirPush({ suportado: true, ligado: inscricao !== null });
  } catch {
    // `serviceWorker.ready` nunca resolve se nao houver SW registrado — em dev
    // ele nem e registrado (`import.meta.env.PROD` no main.ts).
    store.definirPush({ suportado: false, ligado: false });
  }
}

/**
 * Liga a notificacao neste navegador. Devolve o motivo da recusa, ou `null`.
 *
 * A ordem importa e e o oposto da intuitiva: chave do servidor primeiro,
 * permissao depois. Ver o cabecalho.
 */
export async function ligar(): Promise<string | null> {
  if (!suportado()) return "Este navegador não recebe notificações.";
  store.definirPush({ ocupado: true });
  try {
    const { chave } = await api.chavePush();
    if (chave === "") {
      return "A nuvem ainda não está configurada para enviar notificações.";
    }

    const permissao = await Notification.requestPermission();
    if (permissao !== "granted") {
      return permissao === "denied"
        ? "Notificações bloqueadas para este site. Libere nas configurações do navegador."
        : "Permissão de notificação não concedida.";
    }

    const registro = await navigator.serviceWorker.ready;
    const inscricao = await registro.pushManager.subscribe({
      // Obrigatorio no Chrome: uma inscricao que nao mostra notificacao visivel
      // seria rastreamento silencioso, e o navegador a revoga.
      userVisibleOnly: true,
      applicationServerKey: base64UrlParaBytes(chave),
    });
    await api.inscreverPush(inscricao.toJSON() as api.InscricaoPush);
    store.definirPush({ ligado: true });
    return null;
  } catch (erro) {
    return erro instanceof Error ? erro.message : "Não foi possível ativar.";
  } finally {
    store.definirPush({ ocupado: false });
  }
}

/**
 * Desliga neste navegador: apaga no servidor e no navegador, nessa ordem.
 *
 * Servidor primeiro porque `unsubscribe()` destroi o `endpoint`, que e o
 * identificador da linha la — invertida, a ordem deixaria uma linha orfa que so
 * sumiria no primeiro 410, ou seja, na primeira notificacao que o usuario
 * acabou de pedir para nao receber.
 */
export async function desligar(): Promise<string | null> {
  if (!suportado()) return null;
  store.definirPush({ ocupado: true });
  try {
    const registro = await navigator.serviceWorker.ready;
    const inscricao = await registro.pushManager.getSubscription();
    if (inscricao !== null) {
      await api
        .desinscreverPush(inscricao.toJSON() as api.InscricaoPush)
        .catch(() => {
          // Sem rede: o servidor descobre sozinho no primeiro 410. O que da
          // para garantir daqui e parar de receber neste aparelho, e e isso
          // que o `unsubscribe()` abaixo faz.
        });
      await inscricao.unsubscribe();
    }
    store.definirPush({ ligado: false });
    return null;
  } catch (erro) {
    return erro instanceof Error ? erro.message : "Não foi possível desativar.";
  } finally {
    store.definirPush({ ocupado: false });
  }
}

/**
 * base64url -> bytes, para o `applicationServerKey`.
 *
 * `atob` so entende base64 padrao COM padding, e a chave VAPID viaja em
 * base64url sem ele (RFC 8292) — sem esta traducao o `subscribe()` falha com
 * um `InvalidCharacterError` que nao diz nada sobre a causa.
 *
 * O `new ArrayBuffer(...)` explicito nao e enfeite: `new Uint8Array(n)` tem
 * tipo `Uint8Array<ArrayBufferLike>`, que inclui `SharedArrayBuffer` e por isso
 * nao satisfaz o `BufferSource` que o `subscribe()` pede.
 */
function base64UrlParaBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const preenchimento = "=".repeat((4 - (base64url.length % 4)) % 4);
  const base64 = (base64url + preenchimento).replace(/-/g, "+").replace(/_/g, "/");
  const bruto = atob(base64);
  const bytes = new Uint8Array(new ArrayBuffer(bruto.length));
  for (let i = 0; i < bruto.length; i++) bytes[i] = bruto.charCodeAt(i);
  return bytes;
}
